import { useCallback, useEffect, useReducer, useRef } from 'react';
import { getAppointment, getIsLockerOpen, IAppointment, IKeySlot, SignalrEnrollment, SignalrEvent } from '../../../../API/GoMoto';
import * as signalR from '@microsoft/signalr';
import { ActionMap } from '../../../../API/GoMoto.types';

interface ILockerState {
    complete: boolean;
    error: boolean;
    pickedUp: boolean;
    lockerClosed: boolean;
}

enum LockerDispatchTypes {
    SetComplete = 'LOCKER_SET_STATE',
    SetError = 'LOCKER_SET_ERROR',
    SetPickedUp = 'LOCKER_PICKED_UP',
    SetLockerClosed = 'LOCKER_CLOSED'
}

type LockerPayload = {
    [LockerDispatchTypes.SetComplete]: boolean;
    [LockerDispatchTypes.SetError]: boolean;
    [LockerDispatchTypes.SetPickedUp]: never;
    [LockerDispatchTypes.SetLockerClosed]: never;
};
type LockerActions = ActionMap<LockerPayload>[keyof ActionMap<LockerPayload>];

/**
 * Waits for the key to be picked up and the locker to close before reporting completion. The pickup
 * is considered done once the appointment's keyStatus is 'picked up', or 'reopen requested pickup'
 * when the customer reopened the locker after an earlier pickup.
 *
 * @param apptKey the appointment to poll.
 * @param kioskKey the kiosk hosting the locker, used to query the locker's open/closed state.
 * @param lockerNumber the locker number to query for the open/closed state.
 */
export const useIsLockerOpComplete = (apptKey: string = '', kioskKey: string = '', lockerNumber: number = 0) => {
    const hubConnection = useRef<signalR.HubConnection | undefined>(undefined);
    const retryHubConnectionTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

    const [state, dispatch] = useReducer(
        (state: ILockerState, action: LockerActions) => {
            switch (action.type) {
                case LockerDispatchTypes.SetLockerClosed:
                    return {
                        ...state,
                        lockerClosed: true,
                    };
                case LockerDispatchTypes.SetComplete:
                    return {
                        ...state,
                        complete: action.payload,
                    };
                case LockerDispatchTypes.SetError:
                    return {
                        ...state,
                        error: action.payload,
                    };
                case LockerDispatchTypes.SetPickedUp:
                    return {
                        ...state,
                        pickedUp: true,
                    };
                default:
                    return state;
            }
        },
        {
            complete: false,
            error: false,
            lockerClosed: false,
            pickedUp: false,
        },
    );

    const checkIfAlreadyCompleted = useCallback(() => {
        getAppointment(apptKey)
            .then((data: IAppointment | null) => {
                if (!data) {
                    dispatch({
                        type: LockerDispatchTypes.SetError,
                        payload: true,
                    });
                }
                else if (data.keyStatus === 'picked up') {
                    dispatch({ type: LockerDispatchTypes.SetPickedUp });

                    getIsLockerOpen(kioskKey, lockerNumber)
                        .then((isOpen) => {
                            if (!isOpen) {
                                dispatch({ type: LockerDispatchTypes.SetLockerClosed });
                            }
                        });
                }
                else if (data.keyStatus === 'reopen requested pickup') {
                    /*
                     * The key was already picked up and the customer chose to reopen the locker. Treat the
                     * pickup as done, but wait for the LockerStatus close event from the reopen instead of
                     * reading the current locker state, which still reads closed from before the reopen and
                     * would otherwise complete immediately and skip the rescan.
                     */
                    dispatch({ type: LockerDispatchTypes.SetPickedUp });
                }
            });
    }, [apptKey, kioskKey, lockerNumber]);

    const subscribeToEvents = useCallback(() => {
        if (hubConnection.current && hubConnection.current.connectionId) {
            hubConnection.current.invoke(SignalrEnrollment.AppointmentKeyPickedUp, apptKey);
            hubConnection.current.on(SignalrEvent.AppointmentKeyPickedUp, () => {
                dispatch({ type: LockerDispatchTypes.SetPickedUp });
            });

            hubConnection.current.invoke(SignalrEnrollment.LockerStatus, apptKey);
            hubConnection.current.on(SignalrEvent.LockerStatus, (data: string) => {
                const keySlot: IKeySlot = JSON.parse(data);
                if (keySlot && keySlot.keySlotNumber && keySlot.kioskKey && !keySlot.isOpen) {
                    dispatch({ type: LockerDispatchTypes.SetLockerClosed });
                }
            });
        }
    }, [apptKey]);

    const startHubConnection = useCallback(async () => {
        retryHubConnectionTimeout.current = undefined;

        if (hubConnection.current) {
            try {
                await hubConnection.current.start();
                console.assert(hubConnection.current.state === signalR.HubConnectionState.Connected);
                subscribeToEvents();
            }
            catch {
                retryHubConnectionTimeout.current = setTimeout(() => startHubConnection(), 5000);
            }
        }
    }, [subscribeToEvents]);

    useEffect(() => {
        if (state.pickedUp && state.lockerClosed) {
            dispatch({
                type: LockerDispatchTypes.SetComplete,
                payload: true,
            });
        }
    }, [state.pickedUp, state.lockerClosed]);

    useEffect(() => {
        if (apptKey && !hubConnection.current) {
            hubConnection.current = new signalR.HubConnectionBuilder()
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: () => 5000,
                })
                .withUrl(process.env.REACT_APP_GOMOTO_API_ROOT + '/signalr', { withCredentials: false })
                .build();

            hubConnection.current.onreconnected(() => {
                subscribeToEvents();

                // Check if locker op was completed while SignalR was disconnected
                checkIfAlreadyCompleted();
            });

            startHubConnection();
        }

        return () => {
            if (retryHubConnectionTimeout.current) {
                clearTimeout(retryHubConnectionTimeout.current);
            }

            if (hubConnection.current) {
                hubConnection.current.off(SignalrEvent.LockerStatus);
                hubConnection.current.off(SignalrEvent.AppointmentKeyPickedUp);
                hubConnection.current.stop()
                    .catch((error) => console.log(error));
                hubConnection.current = undefined;
            }
        };
    }, [apptKey, checkIfAlreadyCompleted, subscribeToEvents, startHubConnection]);

    useEffect(() => {
        if (!state.complete) {
            checkIfAlreadyCompleted();
        }
    }, [state.complete, checkIfAlreadyCompleted]);

    return {
        complete: state.complete,
        error: state.error,
    };
};
